<template>
    <div
        class="z-accordion-item"
    >
        <button
            @click="onClick"
            class="z-accordion-item-header"
            :class="[
                `z-accordion-item-header--size-${size}`,
                { 'is-active': isOpen }
            ]"
        >
            <z-icon
                class="z-accordion-item-header__icon"
                v-if="showFolderIcon || icon"
                :name="icon ? icon : 'accordions/folders'"
            />
            <span class="z-accordion-item-header__text"><slot name="header"/></span>
            <span class="z-accordion-item-header__button">
                <z-icon v-if="isOpen" name="accordions/minus" />
                <z-icon v-else name="accordions/plus" />
            </span>

        </button>

        <div v-show="isOpen" class="z-accordion-item__body">
            <z-accordion-panel
                :size="size"
                :vertical-padding="accordionPanel?.['vertical-padding']"
                :horizontal-padding="accordionPanel?.['horizontal-padding']"
                :background="accordionPanel?.background"
                :borders="accordionPanel?.borders"
            >
                <slot name="body"/>
            </z-accordion-panel>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onBeforeMount, provide } from 'vue'

const props = defineProps({
    id: {
        type: String,
        required: true
    },
    showFolderIcon: {
        type: Boolean,
        default: false
    },
    icon: {
        type: String
    },
    accordionPanel: {
        type: Object
    }
})

const {
    toggleItem,
    openedItems,
    addItem,
    size
} = inject('accordion')

const onClick = () => {
    toggleItem(props.id)
}

const isOpen = computed(() => {
    return openedItems.value.includes(props.id)
})

provide('accordion-item', {
    itemId: props.id
})

onBeforeMount(() => {
    addItem(props.id)
})
</script>

<style lang="scss">
@import './tokens.scss';

.z-accordion-item-header {
    --bg-color: var(--header-color-bg-default);
    --text-color: var(--header-color-text-default);
    --line-height: 1.2;
    --font-weight: var(--font-weight-l);

    &:hover {
        --bg-color: var(--header-color-bg-hover);
        --text-color: var(--header-color-text-hover);
    }

    &.is-active {
        --bg-color: var(--header-color-bg-active);
        --text-color: var(--header-color-text-active);
    }

    &--size-l {
        --padding: var(--padding-l);
        --icon-spacer: var(--icon-spacer-l);
        --font-size: var(--font-size-l);

        @include breakpoint(laptop) {
            --padding: var(--padding-m);
            --icon-spacer: var(--icon-spacer-m);
            --font-size: var(--font-size-m);
        }
    }

    &--size-m {
        --padding: var(--padding-m);
        --icon-spacer: var(--icon-spacer-m);
        --font-size: var(--font-size-m);
    }

    &--size-s {
        --padding: var(--padding-s);
        --icon-spacer: var(--icon-spacer-s);
        --font-size: var(--font-size-s);
    }
}

.z-accordion-item-header {
    border: none;
    width: 100%;
    display: block;
    text-align: left;
    background: var(--bg-color);
    color: var(--text-color);
    padding: var(--padding);
    font-size: var(--font-size);
    gap: var(--icon-spacer);

    outline: none;
    border-top: var(--border-width) solid var(--border-color);
    font-weight: var(--font-weight);
    line-height: var(--line-height);
    display: flex;
    align-items: center;
    transition: border-color $transition;

    &__text {
        max-width: calc(100% - (1em * var(--line-height)) - var(--icon-spacer));
        flex-grow: 1;
    }

    &__icon {
        flex-shrink: 0;
        width: calc(1em * var(--line-height));
        height: calc(1em * var(--line-height));
        align-self: flex-start;

        path {
            fill: currentColor;
            transition: fill $transition;
        }

        &.z-icon{
            @include breakpoint(mobile){
                display: none;
            }
        }
    }

    &__button {
        max-width: 32px;
        max-height: 32px;
        width: calc(1em * var(--line-height));
        height: calc(1em * var(--line-height));
        display: flex;
        flex-shrink: 0;

        .z-icon {
            width: 100%;
            height: 100%;
        }
    }
}


.z-accordion-panel {
    .z-accordion-item {
        &:first-child {
            .z-accordion-item-header {
                // border-top: none;
            }
        }

        &:last-child {
            .z-accordion-item-header {
                // outline: 1px solid red;
                border-bottom: none;
            }
        }
    }
}

.z-accordion-item {
    .z-accordion-item {
        outline: 1 solid red;
    }

    &:last-child {
        > .z-accordion-item-header {
            border-bottom: var(--border-width) solid var(--border-color);

            &.is-active {
                border-bottom: none;
            }
        }
    }
}
</style>
